import React from 'react'

import cx from 'classnames'
import debounce from 'lodash/debounce'

import { Div, IconButton, Input, Span } from '@driftt/tide-core'
import { Select } from '@driftt/tide-selects'
import { access } from '@driftt/traffic-control'

import { GlobalFilterSetting } from 'api/AccountActivityAPI/types'
import {
  DatePicker,
  DateRangePicker,
  getDefaultDate,
  getDefaultRange
} from 'components/DateRangePicker'
import { GlobalFilterSettingType } from 'hooks/useAdvancedPreferences'
import useFilterAttributeOptions from 'hooks/useFilterAttributeOptions'
import { useGlobalFilters } from 'hooks/useGlobalFilters'
import {
  FilterableEntity,
  FilterConfigAndAvailableValues,
  FilterOperator,
  FilterPlurality,
  FilterValues,
  FilterValueType,
  operatorToString
} from 'utils/filters'

import styles from '../styles.module.css'

interface FilterRowProps {
  filter: FilterConfigAndAvailableValues
  filters: FilterValues[]
  filterConfiguration: FilterConfigAndAvailableValues[]
  comparator: FilterOperator
  selectedValues?: any
  onChangeFilterType: (value: any) => void
  onChangeComparator: (value: any) => void
  onChangeValue: (value: any) => void
  onRemoveFilter: () => void
  calendarOpenDirection?: 'up' | 'down'
  showFormValidation?: boolean
  searchValue: string
  setSearchValue: (value: string) => void
}

const selectStyles = {
  menuPortal: (provided: any) => ({
    ...provided,
    zIndex: 40
  })
}

const invalidFilterMessage = 'Enter a value to apply your filters'

const FilterRow: React.FC<FilterRowProps> = ({
  filter,
  filterConfiguration,
  filters,
  comparator,
  selectedValues,
  onChangeFilterType,
  onChangeComparator,
  onChangeValue,
  onRemoveFilter,
  calendarOpenDirection,
  showFormValidation,
  searchValue,
  setSearchValue
}) => {
  const globalFilters = useGlobalFilters()
  const filterAttributeOptions = useFilterAttributeOptions(filter, searchValue)
  const hasNewPreferencesGate = access.hasGates('prospector_new_preferences_ui')
  const isAlreadySelected = (filter: FilterConfigAndAvailableValues) =>
    !!filters.find(
      (f: FilterValues) =>
        f.filterType === filter.filterType && f.filterSubtype === filter.filterSubtype
    ) ||
    (hasNewPreferencesGate && !!globalFilters.find(
      (gf: GlobalFilterSetting) =>
      {
        if(gf.name === GlobalFilterSettingType.IN_CRM){
          return filter.filterDisplayName === 'In CRM'
        }
        return gf.value.attribute === filter.filterDisplayName
      }
    ))

  const ownerOptions = filterConfiguration
    .filter(({ filterType }) => filterType === FilterableEntity.ACCOUNT_OWNERS)
    .map(f => ({
      value: {
        filterType: f.filterType
      },
      label: f.filterDisplayName,
      isDisabled: isAlreadySelected(f)
    }))

  /**
   * NOTE: currently this returns an empty array and does not render in the select.
   * This is to account for any future filters that get added don't get missed. Also home
   * to any future drift generic filters that aren't included in the select sections.
   */
  const accountOptions = filterConfiguration
    .filter(
      ({ filterType }) =>
        filterType === FilterableEntity.TARGETED ||
        filterType === FilterableEntity.LAST_TOUCHED ||
        filterType === FilterableEntity.IN_CRM
    )
    .map(f => ({
      value: {
        filterType: f.filterType
      },
      label: f.filterDisplayName,
      isDisabled: isAlreadySelected(f)
    }))

  const activityOptions = filterConfiguration
    .filter(
      ({ filterType }) =>
        filterType === FilterableEntity.ACTIVITIES || filterType === FilterableEntity.PAGE_URL
    )
    .map(f => ({
      value: {
        filterType: f.filterType
      },
      label: f.filterDisplayName,
      isDisabled: isAlreadySelected(f)
    }))

  const contactOptions = filterConfiguration
    .filter(
      ({ filterType }) =>
        filterType === FilterableEntity.CONTACT_ROLE ||
        filterType === FilterableEntity.CONTACT_TYPES
    )
    .map(f => ({
      value: {
        filterType: f.filterType
      },
      label: f.filterDisplayName,
      isDisabled: isAlreadySelected(f)
    }))

  const customAttrOptions = filterConfiguration
    .filter(f => f.filterSubtype !== undefined && f.filterType !== FilterableEntity.FIRMOGRAPHICS)
    .map(f => ({
      value: {
        valueType: f.valueType,
        filterType: f.filterType,
        filterSubtype: f.filterSubtype,
        attributeType: f.attributeType
      },
      label: f.filterDisplayName,
      isDisabled: isAlreadySelected(f)
    }))

  const firmographicAttrOptions = filterConfiguration
    .filter(f => f.filterType === FilterableEntity.FIRMOGRAPHICS)
    .map(f => ({
      value: {
        valueType: f.valueType,
        filterType: f.filterType,
        filterSubtype: f.filterSubtype,
        attributeType: f.attributeType
      },
      label: f.filterDisplayName,
      isDisabled: isAlreadySelected(f)
    }))

  const baseOptions = [
    {
      label: 'Owner',
      options: ownerOptions
    },
    {
      label: 'Account',
      options: accountOptions
    },
    {
      label: 'Activity',
      options: activityOptions
    },
    {
      label: 'Contact',
      options: contactOptions
    },
    {
      label: 'Firmographics',
      options: firmographicAttrOptions
    }
  ]

  const filterTypeOptions =
    customAttrOptions.length > 0
      ? [
          ...baseOptions,
          {
            label: 'Custom Attributes',
            options: customAttrOptions
          }
        ]
      : baseOptions

  const onChangeValueDebounced = debounce(onChangeValue, 750)
  const onChangeSearchValueDebounced = debounce(setSearchValue, 300)
  const bodyEl = document.querySelector('body')
  const isFilterValueInvalid = showFormValidation && !filter.getIsFilterValid(selectedValues)

  return (
    <div className={styles.filterContainer} style={filter.gridTemplateStyle}>
      <Select
        className={cx({
          [styles.cursorDisabled]: filterTypeOptions.length === 0,
          filterSelect: true
        })}
        clearable={false}
        creatable={false}
        noResultsText={false}
        searchable={false}
        size={'small'}
        selectStyles={selectStyles}
        value={{ value: filter, label: filter.filterDisplayName }}
        onChange={onChangeFilterType}
        options={filterTypeOptions}
        isDisabled={filters.length === filterConfiguration.length}
        menuPortalTarget={bodyEl}
        menuPlacement="auto"
        minMenuHeight={245}
        maxMenuHeight={400}
      />
      <Select
        className={cx({
          [styles.cursorDisabled]: filter.operators?.length <= 1,
          filterSelect: true
        })}
        clearable={false}
        creatable={false}
        size={'small'}
        noResultsText={false}
        searchable={false}
        selectStyles={selectStyles}
        value={{ value: comparator, label: operatorToString(comparator) }}
        onChange={onChangeComparator}
        options={
          filter.operators?.length > 1
            ? filter.operators.map(operator => ({
                value: operator,
                label: operatorToString(operator)
              }))
            : []
        }
        isDisabled={filter.operators.length < 2}
        menuPortalTarget={bodyEl}
        menuPlacement="auto"
        minMenuHeight={245}
        maxMenuHeight={245}
      />
      {filter.valueType === FilterValueType.STRING && (
        <Div>
          <Select
            className={cx('filterSelect', { [styles.errorSelect]: isFilterValueInvalid })}
            isMulti={filter.plurality === FilterPlurality.MULTIPLE}
            clearable={false}
            creatable={filter.creatable}
            size={'small'}
            selectStyles={selectStyles}
            value={selectedValues}
            onChange={onChangeValue}
            options={filterAttributeOptions}
            onInputChange={onChangeSearchValueDebounced}
            menuPortalTarget={bodyEl}
            menuPlacement="auto"
            minMenuHeight={245}
            maxMenuHeight={245}
            required
          />
          {isFilterValueInvalid && <Span className={styles.redText}>{invalidFilterMessage}</Span>}
        </Div>
      )}
      {filter.valueType === FilterValueType.NUMBER && (
        <Div>
          <Input
            min={0}
            className={styles.numberInput}
            type="number"
            defaultValue={selectedValues !== undefined ? selectedValues : ''}
            onChange={(e: any) =>
              onChangeValueDebounced(e.target.value ? Number(e.target.value) : undefined)
            }
          />
          {isFilterValueInvalid && <Span className={styles.redText}>{invalidFilterMessage}</Span>}
        </Div>
      )}
      {filter.valueType === FilterValueType.DATE && comparator === FilterOperator.BETWEEN && (
        <DateRangePicker
          onDateRangeChange={onChangeValue}
          dateRange={selectedValues || getDefaultRange()}
          openDirection={calendarOpenDirection}
          useFilterModalStyle
        />
      )}

      {filter.valueType === FilterValueType.DATE && comparator === FilterOperator.EQUALS && (
        <DatePicker
          //@ts-ignore
          onChangeDate={onChangeValue}
          date={selectedValues || getDefaultDate()}
          openDirection={calendarOpenDirection}
        />
      )}
      <IconButton
        onClick={onRemoveFilter}
        className={styles.closeMargin}
        icon="cancel-circle"
        height={18}
        width={18}
      />
    </div>
  )
}

export default FilterRow
